import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Check, Lightning, Thermometer, Mountains, Leaf, CurrencyDollar } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  earthquake: { Icon: Mountains, color: "text-orange-400", bg: "bg-orange-500/20" },
  weather: { Icon: Thermometer, color: "text-sky-400", bg: "bg-sky-500/20" },
  ndvi: { Icon: Leaf, color: "text-emerald-400", bg: "bg-emerald-500/20" },
  carbon: { Icon: CurrencyDollar, color: "text-amber-400", bg: "bg-amber-500/20" },
  system: { Icon: Bell, color: "text-violet-400", bg: "bg-violet-500/20" },
};

const SEVERITY_COLOR = {
  critical: "border-l-red-500",
  warning: "border-l-amber-500",
  info: "border-l-sky-500",
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data?.notifications || []);
      setUnread(res.data?.unread || 0);
    } catch { /* silent */ }
  };

  const markRead = async () => {
    await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
    setUnread(0);
    fetchNotifications();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative bg-stone-800 border border-stone-700 rounded-lg p-1.5 text-stone-400 hover:text-white transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={16} weight={unread > 0 ? "fill" : "bold"} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-full right-0 mt-2 w-80 bg-stone-900 border border-stone-700 rounded-xl shadow-2xl overflow-hidden z-[1200]"
            data-testid="notification-panel"
          >
            <div className="px-4 py-3 border-b border-stone-800 flex items-center justify-between">
              <span className="text-sm font-heading text-white">
                Notifications {unread > 0 && <span className="text-xs text-stone-500">({unread} new)</span>}
              </span>
              {unread > 0 && (
                <button onClick={markRead} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid="mark-all-read">
                  <Check size={12} /> Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[50vh] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell size={24} className="text-stone-700 mx-auto mb-2" />
                  <p className="text-xs text-stone-500">No notifications yet</p>
                </div>
              ) : (
                notifications.slice(0, 20).map((n) => {
                  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.system;
                  const NIcon = cfg.Icon;
                  return (
                    <div
                      key={n.id}
                      className={`px-4 py-3 border-b border-stone-800/50 last:border-0 border-l-2 ${SEVERITY_COLOR[n.severity] || "border-l-transparent"} ${
                        !n.read ? "bg-stone-800/30" : ""
                      }`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={`w-6 h-6 rounded-md ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <NIcon size={12} weight="bold" className={cfg.color} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-white font-medium">{n.title}</p>
                          <p className="text-[10px] text-stone-500 mt-0.5 leading-relaxed">{n.message}</p>
                          <p className="text-[9px] text-stone-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                        {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0 mt-1.5" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
